// @ts-nocheck
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { childSchema, personSchema, addressSchema, petSchema } from '@/lib/schema';
import { useWillStore } from '@/lib/store';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { Button } from '@/app/components/ui/Button';
import { Modal } from '@/app/components/ui/Modal';
import { StepWrapper } from '../StepWrapper';
import { FormNavigation } from '../FormNavigation';
import { AddressSection } from '../AddressSection';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { Baby, Check, PawPrint, User, Pencil } from 'lucide-react';
import { ActionCard } from '@/app/components/ui/ActionCard';
import * as yup from 'yup';
import { RELATIONSHIP_OPTIONS } from '@/lib/constants';
import { useSavedPeople, SAVED_PEOPLE_PRESETS } from '@/lib/hooks/useSavedPeople';

const childrenGuardiansStepSchema = yup.object({
  formStep: yup.number().default(1),
  hasChildren: yup.boolean().required('Please select whether you have children'),
  children: yup.array().of(childSchema).when('hasChildren', {
    is: true,
    then: (schema) => schema.min(1, 'Please add at least one child').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  appointGuardians: yup.boolean().when(['hasChildren', 'formStep'], {
    is: (hasChildren: boolean, step: number) => hasChildren === true && step === 2,
    then: (schema) => schema.required('Please select whether you want to appoint guardians for your children'),
    otherwise: (schema) => schema.notRequired(),
  }),
  guardians: yup.array().of(personSchema).when(['hasChildren', 'appointGuardians', 'formStep'], {
    is: (hasChildren: boolean, appointGuardians: boolean, step: number) => hasChildren === true && appointGuardians === true && step === 2,
    then: (schema) => schema.min(1, 'Please appoint at least one guardian for your children').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  hasPets: yup.boolean().when('formStep', {
    is: (step: number) => step >= 3,
    then: (schema) => schema.required('Please select whether you have pets'),
    otherwise: (schema) => schema.notRequired(),
  }),
  pets: yup.array().of(petSchema).when(['hasPets', 'formStep'], {
    is: (hasPets: boolean, step: number) => hasPets === true && step >= 3,
    then: (schema) => schema.min(1, 'Please add at least one pet').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
  appointPetGuardians: yup.boolean().when(['hasPets', 'formStep'], {
    is: (hasPets: boolean, step: number) => hasPets === true && step === 4,
    then: (schema) => schema.required('Please select whether you want to appoint guardians for your pets'),
    otherwise: (schema) => schema.notRequired(),
  }),
  petGuardians: yup.array().of(personSchema).when(['hasPets', 'appointPetGuardians', 'formStep'], {
    is: (hasPets: boolean, appointPetGuardians: boolean, step: number) => hasPets === true && appointPetGuardians === true && step === 4,
    then: (schema) => schema.min(1, 'Please appoint at least one guardian for your pets').required(),
    otherwise: (schema) => schema.notRequired().default([]),
  }),
});

type ChildrenGuardiansData = yup.InferType<typeof childrenGuardiansStepSchema>;

import { Checkbox } from '@/app/components/ui/Checkbox';

interface AddChildFormProps {
  onSave: (data: { fullName: string; dateOfBirth: string }) => void;
}

const AddChildForm = ({ onSave, initialData, onDelete }: AddChildFormProps & { initialData?: { fullName: string; dateOfBirth: string }; onDelete?: () => void }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(childSchema),
    mode: 'onChange',
    defaultValues: initialData || { fullName: '', dateOfBirth: '' },
  });

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="childFullName" className="text-base font-medium text-gray-700">Full Name</Label>
          <Input
            id="childFullName"
            {...register('fullName')}
            placeholder=""
          />
          {errors.fullName && (
            <p className="text-destructive text-sm">{errors.fullName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="childDateOfBirth" className="text-base font-medium text-gray-700">Date of Birth</Label>
          <Input
            id="childDateOfBirth"
            type="tel"
            data-type="date-text"
            placeholder="dd/mm/yyyy"
            {...register('dateOfBirth')}
          />
          {errors.dateOfBirth && (
            <p className="text-destructive text-sm">{errors.dateOfBirth.message}</p>
          )}
        </div>
      </div>

      <div className="pt-4">
        {onDelete ? (
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onDelete} className="flex-1">Delete</Button>
            <Button type="submit" disabled={!isValid} variant="primary" className="flex-1">
              Save
            </Button>
          </div>
        ) : (
          <Button type="submit" disabled={!isValid} variant="primary" className="w-full">
            Save
          </Button>
        )}
      </div>
    </form>
  );
};

interface AddPetFormProps {
  onSave: (data: { name: string }) => void;
}

const AddPetForm = ({ onSave, initialData, onDelete }: AddPetFormProps & { initialData?: { name: string }; onDelete?: () => void }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(petSchema),
    mode: 'onChange',
    defaultValues: initialData || { name: '' },
  });

  return (
    <form onSubmit={handleSubmit(onSave)} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="petName" className="text-base font-medium text-gray-700">Pet Name</Label>
          <Input
            id="petName"
            {...register('name')}
            placeholder=""
          />
          {errors.name && (
            <p className="text-destructive text-sm">{errors.name.message}</p>
          )}
        </div>
      </div>

      <div className="pt-4">
        {onDelete ? (
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={onDelete} className="flex-1">Delete</Button>
            <Button type="submit" disabled={!isValid} variant="primary" className="flex-1">
              Save
            </Button>
          </div>
        ) : (
          <Button type="submit" disabled={!isValid} variant="primary" className="w-full">
            Save
          </Button>
        )}
      </div>
    </form>
  );
};


import { PersonForm } from '../shared/PersonForm';
import { QuickAddPeople } from '../shared/QuickAddPeople';

export const ChildrenGuardians = () => {
  const { data, setData, setStep, setStepBack, isEditingFromReview, setIsEditingFromReview, navigatingBack, clearNavigatingBack, updatePerson, addManuallyAddedPerson, removePersonEverywhere } = useWillStore();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<ChildrenGuardiansData>({
    resolver: yupResolver(childrenGuardiansStepSchema) as any,
    mode: 'onBlur',
    // Keep values from hidden sub-steps so we don't drop the user's Yes/No
    // answers or guardian selections when they navigate away and return.
    shouldUnregister: false,
    defaultValues: {
      formStep: 1, // Always start at beginning when entering this step
      hasChildren: data.hasChildren,
      children: data.children ?? [],
      hasPets: data.hasPets,
      pets: data.pets ?? [],
      appointGuardians: data.appointGuardians,
      guardians: data.guardians ?? [],
      appointPetGuardians: data.appointPetGuardians,
      petGuardians: data.petGuardians ?? [],
    },
  });

  // Sync form with store data when it changes (e.g. after login/rehydration)
  // Normalize guardians for stable comparisons (strip react-hook-form ids)
  const normalizeGuardians = (items: any[] = []) =>
    items.map(g => ({
      fullName: g.fullName || '',
      relationship: g.relationship || '',
      forChild: (g as any).forChild || '',
      address: g.address || {},
    }));

  // Avoid infinite reset loops by hydrating only when incoming data differs from current form state
  const lastHydrateSignature = useRef<string | null>(null);
  useEffect(() => {
    const hasIncoming =
      data.children || data.pets || data.guardians || data.petGuardians || data.hasChildren !== undefined || data.hasPets !== undefined;
    if (!hasIncoming) return;

    const normalizeChildren = (items: any[] = []) =>
      items.map(c => ({ fullName: c.fullName || '', dateOfBirth: c.dateOfBirth || '' }));
    const normalizePets = (items: any[] = []) =>
      items.map(p => ({ name: p.name || '' }));

    const targetState = {
      formStep: getValues('formStep'),
      hasChildren: data.hasChildren,
      children: normalizeChildren(data.children ?? []),
      hasPets: data.hasPets,
      pets: normalizePets(data.pets ?? []),
      appointGuardians: data.appointGuardians,
      guardians: normalizeGuardians(data.guardians ?? []),
      appointPetGuardians: data.appointPetGuardians,
      petGuardians: normalizeGuardians(data.petGuardians ?? []),
    };

    const currentState = {
      formStep: getValues('formStep'),
      hasChildren: getValues('hasChildren'),
      children: normalizeChildren(getValues('children') ?? []),
      hasPets: getValues('hasPets'),
      pets: normalizePets(getValues('pets') ?? []),
      appointGuardians: getValues('appointGuardians'),
      guardians: normalizeGuardians(getValues('guardians') ?? []),
      appointPetGuardians: getValues('appointPetGuardians'),
      petGuardians: normalizeGuardians(getValues('petGuardians') ?? []),
    };

    const targetSignature = JSON.stringify(targetState);
    const currentSignature = JSON.stringify(currentState);

    // If already in sync, skip reset
    if (targetSignature === currentSignature) return;

    // Also skip if we've already hydrated this exact target
    if (lastHydrateSignature.current === targetSignature) return;
    lastHydrateSignature.current = targetSignature;

    reset(targetState);
  }, [data.children, data.pets, data.guardians, data.petGuardians, data.hasChildren, data.hasPets, data.appointGuardians, data.appointPetGuardians, reset, getValues]);

  // Handle navigating back from next section - go to last sub-step
  useEffect(() => {
    if (navigatingBack) {
      // Go to the last relevant sub-step (4 = pet guardians section)
      setValue('formStep', 4);
      clearNavigatingBack();
    }
  }, [navigatingBack, clearNavigatingBack, setValue]);


  const hasChildren = watch('hasChildren');
  const hasPets = watch('hasPets');
  const appointGuardians = watch('appointGuardians');
  const appointPetGuardians = watch('appointPetGuardians');
  const formStep = watch('formStep');

  const guardians = useWatch({
    control,
    name: 'guardians',
  });

  const petGuardians = useWatch({
    control,
    name: 'petGuardians',
  });

  const {
    fields: childFields,
    append: appendChild,
    remove: removeChild,
    update: updateChild,
  } = useFieldArray({
    control,
    name: 'children',
  });

  const {
    fields: petFields,
    append: appendPet,
    remove: removePet,
    update: updatePet,
  } = useFieldArray({
    control,
    name: 'pets',
  });

  const {
    fields: guardianFields,
    append: appendGuardian,
    remove: removeGuardian,
    update: updateGuardian,
  } = useFieldArray({
    control,
    name: 'guardians',
  });

  const {
    fields: petGuardianFields,
    append: appendPetGuardian,
    remove: removePetGuardian,
  } = useFieldArray({
    control,
    name: 'petGuardians',
  });

  // Persist guardian selections and flag so they survive navigation away/return
  useEffect(() => {
    if (appointGuardians !== undefined) {
      const existingGuardians = data.guardians ?? [];
      const currentSignature = JSON.stringify({
        appointGuardians,
        guardians: guardianFields,
      });
      const existingSignature = JSON.stringify({
        appointGuardians: data.appointGuardians,
        guardians: existingGuardians,
      });
      if (currentSignature === existingSignature) return;

      setData({
        appointGuardians,
        guardians: guardianFields,
      });
    }
  }, [appointGuardians, guardianFields, setData]);

  // Persist pet guardian selections and flag
  useEffect(() => {
    if (appointPetGuardians !== undefined) {
      const existingPetGuardians = data.petGuardians ?? [];
      const currentSignature = JSON.stringify({
        appointPetGuardians,
        petGuardians: petGuardianFields,
      });
      const existingSignature = JSON.stringify({
        appointPetGuardians: data.appointPetGuardians,
        petGuardians: existingPetGuardians,
      });
      if (currentSignature === existingSignature) return;

      setData({
        appointPetGuardians,
        petGuardians: petGuardianFields,
      });
    }
  }, [appointPetGuardians, petGuardianFields, setData]);

  const [isAddChildModalOpen, setIsAddChildModalOpen] = useState(false);
  const [isAddPetModalOpen, setIsAddPetModalOpen] = useState(false);
  const [editingChildIndex, setEditingChildIndex] = useState<number | null>(null);
  const [editingPetIndex, setEditingPetIndex] = useState<number | null>(null);
  const [isAddGuardianModalOpen, setIsAddGuardianModalOpen] = useState(false);
  const [isAddPetGuardianModalOpen, setIsAddPetGuardianModalOpen] = useState(false);
  const [guardianForChild, setGuardianForChild] = useState<string | null>(null); // Track which child we're adding a guardian for

  // Parental responsibility check modal state
  const [parentalCheckModalOpen, setParentalCheckModalOpen] = useState(false);
  const [pendingGuardianSelection, setPendingGuardianSelection] = useState<{
    childName: string;
    person: { fullName: string; relationship: string; address?: any };
  } | null>(null);

  // State for editing quick add people
  const [editingQuickAddPerson, setEditingQuickAddPerson] = useState<any>(null);
  const [tempQuickAddPerson, setTempQuickAddPerson] = useState<any>(null);
  const [manualQuickAddAddressMode, setManualQuickAddAddressMode] = useState(false);

  const manuallyAddedPeople = data.manuallyAddedPeople || [];

  // Use shared hook to get saved people for quick-add (spouse, executors, etc.)
  const savedPeople = useSavedPeople(SAVED_PEOPLE_PRESETS.guardians);

  // Get guardians for a specific child (use field array as source of truth)
  const getGuardiansForChild = (childName: string) => {
    return (guardianFields as any[])?.filter(g => (g as any).forChild === childName) || [];
  };

  // Helper: parse DD/MM/YYYY to determine if under 18
  const isUnder18 = (dobStr?: string) => {
    if (!dobStr || !/^\d{2}\/\d{2}\/\d{4}$/.test(dobStr)) return true; // treat unknown as under 18 for safety
    const [dayStr, monthStr, yearStr] = dobStr.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const year = parseInt(yearStr, 10);
    const dob = new Date(year, month, day);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age < 18;
  };





  // Check if a person is selected as guardian for a specific child
  const isGuardianSelectedForChild = (childName: string, personName: string) => {
    const childGuardians = getGuardiansForChild(childName);
    return childGuardians?.some(g => g.fullName === personName) || false;
  };

  // Track manually added guardian
  const trackManuallyAddedGuardian = (person: { fullName: string; relationship: string; address?: any }) => {
    if (!savedPeople.some(p => p.fullName === person.fullName)) {
      addManuallyAddedPerson(person);
    }
  };

  // Toggle guardian selection for a specific child
  const toggleGuardianForChild = (childName: string, person: { fullName: string; relationship: string; address?: any }) => {
    // Prevent appointing the same child or any minor as guardian
    const childRecord = childFields.find(child => child.fullName === person.fullName);
    if (childRecord) {
      if (childRecord.fullName === childName) {
        setError('guardians', { type: 'manual', message: 'A child cannot be appointed as their own guardian.' });
        return;
      }
      if (isUnder18(childRecord.dateOfBirth)) {
        setError('guardians', { type: 'manual', message: 'Guardians must be over 18.' });
        return;
      }
    }

    clearErrors('guardians');
    if (isGuardianSelectedForChild(childName, person.fullName)) {
      handleRemoveGuardianForChild(childName, person.fullName);
    } else {
      // Show parental responsibility check modal before adding
      setPendingGuardianSelection({ childName, person });
      setParentalCheckModalOpen(true);
    }
  };

  // Handle parental responsibility answer
  const handleParentalResponsibilityAnswer = (hasParentalResponsibility: boolean) => {
    if (!pendingGuardianSelection) return;

    if (hasParentalResponsibility) {
      // They already have parental responsibility - don't add, just close modal
      setParentalCheckModalOpen(false);
      setPendingGuardianSelection(null);
    } else {
      // They don't have parental responsibility - add them as guardian
      handleQuickAddGuardianForChild(pendingGuardianSelection.childName, pendingGuardianSelection.person);
      setParentalCheckModalOpen(false);
      setPendingGuardianSelection(null);
    }
  };

  // Check if a person is selected as a pet guardian
  const isPetGuardianSelected = (personName: string) => {
    return (petGuardians as any[])?.some(g => g.fullName === personName) || false;
  };

  // Track manually added pet guardian
  const trackManuallyAddedPetGuardian = (person: { fullName: string; relationship: string; address?: any }) => {
    if (!savedPeople.some(p => p.fullName === person.fullName)) {
      addManuallyAddedPerson(person);
    }
  };

  // Toggle pet guardian selection
  const togglePetGuardian = (person: { fullName: string; relationship: string; address?: any }) => {
    const childRecord = childFields.find(child => child.fullName === person.fullName);
    if (childRecord && isUnder18(childRecord.dateOfBirth)) {
      setError('petGuardians', { type: 'manual', message: 'Pet guardians must be over 18.' });
      return;
    }

    clearErrors('petGuardians');
    const existingIndex = petGuardianFields?.findIndex(g => g.fullName === person.fullName);
    if (existingIndex !== undefined && existingIndex >= 0) {
      removePetGuardian(existingIndex);
    } else {
      const fallbackAddress = person.address || data.personalDetails?.address || { line1: '', line2: '', city: '', postcode: '' };
      appendPetGuardian({
        fullName: person.fullName,
        relationship: 'Guardian',
        address: fallbackAddress
      });
    }
  };

  const handleHasChildrenChange = (value: boolean) => {
    setValue('hasChildren', value, { shouldValidate: true });
    if (value) {
      setValue('appointGuardians', undefined);
    } else {
      setValue('appointGuardians', undefined);
      setValue('children', []);
      setValue('guardians', []);
    }
  };

  const handleHasPetsChange = (value: boolean) => {
    setValue('hasPets', value, { shouldValidate: true });
    if (value) {
      setValue('appointPetGuardians', undefined);
    } else {
      setValue('appointPetGuardians', undefined);
      setValue('pets', []);
      setValue('petGuardians', []);
    }
  };

  const handleAddChild = (data: { fullName: string; dateOfBirth: string }) => {
    if (editingChildIndex !== null) {
      const previousName = childFields[editingChildIndex]?.fullName;
      updateChild(editingChildIndex, data);
      // If the child is renamed, propagate the new name everywhere
      if (previousName && previousName !== data.fullName) {
        guardianFields.forEach((g, idx) => {
          if ((g as any).forChild === previousName) {
            if (updateGuardian) {
              updateGuardian(idx, { ...g, forChild: data.fullName } as any);
            } else {
              setValue(`guardians.${idx}.forChild`, data.fullName);
            }
          }
        });
        updatePerson(previousName, { fullName: data.fullName, relationship: 'Child' });
      }
      setEditingChildIndex(null);
    } else {
      appendChild(data);
    }
    setIsAddChildModalOpen(false);
  };

  const handleRemoveChildEntry = (index: number, fullName?: string) => {
    removeChild(index);
    if (fullName) {
      // Remove everywhere to keep other sections (executors, beneficiaries, etc.) in sync
      removePersonEverywhere(fullName);
    }
  };

  const handleAddPet = (data: { name: string }) => {
    if (editingPetIndex !== null) {
      updatePet(editingPetIndex, data);
      setEditingPetIndex(null);
    } else {
      appendPet(data);
    }
    setIsAddPetModalOpen(false);
  };

  const handleOpenAddChildModal = () => {
    setEditingChildIndex(null);
    setIsAddChildModalOpen(true);
  };

  const handleOpenAddPetModal = () => {
    setEditingPetIndex(null);
    setIsAddPetModalOpen(true);
  };

  const handleOpenEditChildModal = (index: number) => {
    setEditingChildIndex(index);
    setIsAddChildModalOpen(true);
  };

  const handleOpenEditPetModal = (index: number | null) => {
    setEditingPetIndex(index);
    setIsAddPetModalOpen(true);
  };

  const handleOpenAddGuardianModal = () => {
    setIsAddGuardianModalOpen(true);
  };

  const handleOpenAddPetGuardianModal = () => {
    setIsAddPetGuardianModalOpen(true);
  };

  const handleAddGuardian = (formData: AddGuardianFormData) => {
    const { isOver18, ...guardianData } = formData;
    // Block appointing the same child or any minor as guardian
    const childRecord = childFields.find(child => child.fullName === guardianData.fullName);
    if (childRecord) {
      if (guardianForChild && childRecord.fullName === guardianForChild) {
        setError('guardians', { type: 'manual', message: 'A child cannot be appointed as their own guardian.' });
        setIsAddGuardianModalOpen(false);
        setGuardianForChild(null);
        return;
      }
      if (isUnder18(childRecord.dateOfBirth)) {
        setError('guardians', { type: 'manual', message: 'Guardians must be over 18.' });
        setIsAddGuardianModalOpen(false);
        setGuardianForChild(null);
        return;
      }
    }

    // Show parental responsibility check modal before adding
    if (guardianForChild) {
      setPendingGuardianSelection({
        childName: guardianForChild,
        person: {
          fullName: guardianData.fullName,
          relationship: 'Guardian',
          address: guardianData.address
        }
      });
      setIsAddGuardianModalOpen(false);
      setParentalCheckModalOpen(true);
      // Track the person for quick add
      if (guardianData.fullName) {
        trackManuallyAddedGuardian({
          fullName: guardianData.fullName,
          relationship: 'Guardian',
          address: guardianData.address
        });
      }
    }
    setGuardianForChild(null);
  };

  const handleOpenAddGuardianModalForChild = (childName: string) => {
    setGuardianForChild(childName);
    setIsAddGuardianModalOpen(true);
  };

  const handleQuickAddGuardianForChild = (childName: string, person: { fullName: string; relationship: string; address?: any }) => {
    const childRecord = childFields.find(child => child.fullName === person.fullName);
    if (childRecord) {
      if (childRecord.fullName === childName) {
        setError('guardians', { type: 'manual', message: 'A child cannot be appointed as their own guardian.' });
        return;
      }
      if (isUnder18(childRecord.dateOfBirth)) {
        setError('guardians', { type: 'manual', message: 'Guardians must be over 18.' });
        return;
      }
    }

    clearErrors('guardians');
    const fallbackAddress = person.address || data.personalDetails?.address || { line1: '', line2: '', city: '', postcode: '' };
    appendGuardian({
      fullName: person.fullName,
      relationship: 'Guardian',
      address: fallbackAddress,
      forChild: childName
    } as any);
  };

  const handleRemoveGuardianForChild = (childName: string, guardianName: string) => {
    const index = guardianFields.findIndex(g => g.fullName === guardianName && (g as any).forChild === childName);
    if (index !== -1) {
      removeGuardian(index);
    }
  };

  const handleAddPetGuardian = (data: AddGuardianFormData) => {
    const { isOver18, ...guardianData } = data;
    const childRecord = childFields.find(child => child.fullName === guardianData.fullName);
    if (childRecord && isUnder18(childRecord.dateOfBirth)) {
      setError('petGuardians', { type: 'manual', message: 'Pet guardians must be over 18.' });
      setIsAddPetGuardianModalOpen(false);
      return;
    }

    appendPetGuardian({ ...guardianData, relationship: 'Guardian' });
    // Track manually added person so they persist in quick add even if unticked
    if (guardianData.fullName) {
      trackManuallyAddedPetGuardian({
        fullName: guardianData.fullName,
        relationship: 'Guardian',
        address: guardianData.address
      });
    }
    setIsAddPetGuardianModalOpen(false);
  };

  const handleAppointGuardiansChange = (value: boolean) => {
    setValue('appointGuardians', value, { shouldValidate: true });
    if (!value) {
      setValue('guardians', []);
      setData({ guardianConfirmations: {} });
    }
  };

  const handleAppointPetGuardiansChange = (value: boolean) => {
    setValue('appointPetGuardians', value, { shouldValidate: true });
    if (!value) {
      setValue('petGuardians', []);
    }
  };

  const openEditQuickAddModal = (person: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling selection
    setEditingQuickAddPerson(person);
    setTempQuickAddPerson({
      fullName: person.fullName,
      relationship: person.relationship,
      address: person.address || { line1: '', line2: '', city: '', postcode: '' },
      isOver18: true // Assume adults in quick add
    });
    setManualQuickAddAddressMode(false);
  };

  const handleCloseQuickAddModal = () => {
    setEditingQuickAddPerson(null);
    setTempQuickAddPerson(null);
  };

  const handleSaveQuickAddPerson = () => {
    if (editingQuickAddPerson && tempQuickAddPerson) {
      updatePerson(editingQuickAddPerson.fullName, {
        fullName: tempQuickAddPerson.fullName,
        relationship: tempQuickAddPerson.relationship,
        address: tempQuickAddPerson.address
      });
      handleCloseQuickAddModal();
    }
  };

  const [customModeMap, setCustomModeMap] = useState<Record<string, boolean>>({});

  const handleRelationshipSelect = (index: number, value: string) => {
    const fieldId = guardianFields[index].id;
    if (value === 'Other') {
      setCustomModeMap(prev => ({ ...prev, [fieldId]: true }));
      setValue(`guardians.${index}.relationship`, '');
    } else {
      setCustomModeMap(prev => ({ ...prev, [fieldId]: false }));
      setValue(`guardians.${index}.relationship`, value);
    }
  };

  const clearCustomMode = (index: number) => {
    const fieldId = guardianFields[index].id;
    setCustomModeMap(prev => ({ ...prev, [fieldId]: false }));
    setValue(`guardians.${index}.relationship`, '');
  };

  const onSubmit = (formData: ChildrenGuardiansData) => {
    // Persist explicit Yes/No answers even if fields are unmounted
    const effectiveAppointGuardians =
      formData.appointGuardians ?? appointGuardians ?? data.appointGuardians;
    const effectiveGuardians =
      (guardianFields?.length ?? 0) > 0 ? guardianFields : (data.guardians ?? []);

    const effectiveAppointPetGuardians =
      formData.appointPetGuardians ?? appointPetGuardians ?? data.appointPetGuardians;
    const effectivePetGuardians =
      (petGuardianFields?.length ?? 0) > 0 ? petGuardianFields : (data.petGuardians ?? []);

    const cleanedData: Partial<ChildrenGuardiansData> = {
      hasChildren: formData.hasChildren,
      children: [],
      // Keep guardian answers unless explicitly cleared
      appointGuardians: effectiveAppointGuardians,
      guardians: effectiveAppointGuardians ? effectiveGuardians : [],
      hasPets: formData.hasPets,
      pets: [],
      appointPetGuardians: effectiveAppointPetGuardians,
      petGuardians: effectiveAppointPetGuardians ? effectivePetGuardians : [],
    };

    // Note: formData only contains fields that are actively registered on the page.
    // We need to persist data from previous steps using the fields arrays or store state if not present.
    // However, since we are using useFieldArray, the 'fields' are the source of truth for array data.
    // But boolean flags like 'hasChildren' might need careful handling if unmounting steps.
    // Actually, react-hook-form preserves values if 'shouldUnregister: false' (default is true in v7 but we didn't set it).
    // Let's rely on useWillStore to keep persistent state and re-initialise form with it.

    // Always update children/pets from current fields state or fallback to existing data
    if (childFields.length > 0) {
      cleanedData.hasChildren = true;
      cleanedData.children = childFields.map(f => ({ fullName: f.fullName, dateOfBirth: f.dateOfBirth }));
    } else {
      // Fallback: If not on step 1, assume data might exist in store/props even if fields are empty (e.g. initial render)
      // But fields should be populated by defaultValues.
      // If formData.hasChildren is present (from step 1), use it.
      if (formData.formStep === 1) {
        cleanedData.hasChildren = formData.hasChildren;
      } else {
        // Retain existing value if navigating later steps
        cleanedData.hasChildren = hasChildren;
        if (hasChildren && data.children) {
          cleanedData.children = data.children.map(c => ({
            fullName: c.fullName || '',
            dateOfBirth: c.dateOfBirth || ''
          }));
        }
      }
    }

    if (petFields.length > 0) {
      cleanedData.hasPets = true;
      cleanedData.pets = petFields.map(f => ({ name: f.name }));
    } else {
      if (formData.formStep === 3) {
        cleanedData.hasPets = formData.hasPets;
      } else {
        cleanedData.hasPets = hasPets;
        if (hasPets && data.pets) {
          cleanedData.pets = data.pets.map(p => ({
            name: p.name || ''
          }));
        }
      }
    }

    // Step 1: Children
    if (formData.formStep === 1) {
      // Just move to next step. Validation handled by schema.
      // We need to determine if we skip "Children Guardians" (Step 2).
      // Logic: Show Step 2 ONLY if there are minor children.

      let hasMinorChildren = false;
      if (cleanedData.children) {
        hasMinorChildren = cleanedData.children.some(child => {
          if (!child.dateOfBirth) return false;
          const parts = child.dateOfBirth.split('/');
          if (parts.length !== 3) return false;
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1;
          const year = parseInt(parts[2], 10);
          const dob = new Date(year, month, day);
          const today = new Date();
          let age = today.getFullYear() - dob.getFullYear();
          const m = today.getMonth() - dob.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
            age--;
          }
          return age < 18;
        });
      }

      if (hasMinorChildren) {
        setValue('formStep', 2);
        window.scrollTo(0, 0);
        // We must return here to prevent finishing the form
        // But we should also SAVE the data to the store so it persists if we reload.
        // However, typical multi-step within one component holds state in form.
        // If we want to persist between these internal steps, we can update store partially.
        // Let's update store with what we have.
        setData(cleanedData);
        return;
      } else {
        // No minors -> Skip to Pets (Step 3)
        setValue('formStep', 3);
        window.scrollTo(0, 0);
        setData(cleanedData);
        return;
      }
    }

    // Step 2: Children Guardians
    if (formData.formStep === 2) {
      cleanedData.appointGuardians = effectiveAppointGuardians;
      if (effectiveAppointGuardians) {
        cleanedData.guardians = effectiveGuardians; // or guardianFields
      } else {
        cleanedData.guardians = [];
      }
      // Go to Pets (Step 3)
      setValue('formStep', 3);
      window.scrollTo(0, 0);
      setData(cleanedData);
      return;
    }

    // Step 3: Pets
    if (formData.formStep === 3) {
      if (cleanedData.hasPets) {
        // Go to Pet Guardians (Step 4)
        setValue('formStep', 4);
        window.scrollTo(0, 0);
        setData(cleanedData);
        return;
      } else {
        // Skip Pet Guardians (Step 4) and finish
        // Proceed to final submit
      }
    }

    // Step 4: Pet Guardians
    if (formData.formStep === 4) {
      cleanedData.appointPetGuardians = effectiveAppointPetGuardians;
      if (effectiveAppointPetGuardians) {
        cleanedData.petGuardians = effectivePetGuardians; // or petGuardianFields
      } else {
        cleanedData.petGuardians = [];
      }
    }

    // Ensure we preserve previous step data in cleanedData for final submit if we skipped steps or are gathering everything
    // Actually, we reconstructed cleanedData above from fields.
    // We just need to make sure we don't lose guardians if we are on step 4.
    if (formStep >= 2) {
      cleanedData.appointGuardians = effectiveAppointGuardians;
      cleanedData.guardians = effectiveAppointGuardians ? effectiveGuardians : [];
    }

    if (formStep >= 4) {
      cleanedData.appointPetGuardians = effectiveAppointPetGuardians;
      cleanedData.petGuardians = effectiveAppointPetGuardians ? effectivePetGuardians : [];
    }

    // @ts-ignore
    delete cleanedData.formStep;
    setData(cleanedData);

    if (isEditingFromReview) {
      setIsEditingFromReview(false);
      setStep(5); // Return to Review
    } else {
      // Check if all sections are now complete - if so, go to Review
      // Use setTimeout to ensure state has updated before checking
      setTimeout(() => {
        // Import is at top of file
        const { isAllSectionsComplete } = require('@/lib/store');
        if (isAllSectionsComplete()) {
          setStep(5); // Go to Review
        } else {
          setStep(2); // Move to next Main Step (Beneficiaries)
        }
      }, 0);
    }
  };

  const handleBack = () => {
    if (formStep === 4) {
      setValue('formStep', 3);
      window.scrollTo(0, 0);
    } else if (formStep === 3) {
      // If we have minor children, go back to Step 2. Else Step 1.
      const hasMinorChildren = childFields.some(child => {
        if (!child.dateOfBirth) return false;
        const parts = child.dateOfBirth.split('/');
        if (parts.length !== 3) return false;
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const dob = new Date(year, month, day);
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const m = today.getMonth() - dob.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
          age--;
        }
        return age < 18;
      });

      if (hasMinorChildren) {
        setValue('formStep', 2);
      } else {
        setValue('formStep', 1);
      }
      window.scrollTo(0, 0);
    } else if (formStep === 2) {
      setValue('formStep', 1);
      window.scrollTo(0, 0);
    } else {
      setStepBack(0); // Go back to last page of Personal Details
    }
  };

  const getGuardianTitle = () => {
    if (formStep === 4) return "Pet Guardians";
    if (formStep === 3) return "Pets";
    if (formStep === 2) return "Guardians";
    return "Children";
  };

  const getGuardianDescription = () => {
    if (formStep === 1) {
      return (
        <div className="space-y-4">
          <ul className="list-disc pl-5 space-y-2">
            <li>List all your biological and legally adopted children.</li>
            <li>Don’t list stepchildren here; you can add them later if you want to include them.</li>
            <li>If a child has a disability or may need support after 18, speak with a specialist solicitor to ensure their needs are covered.</li>
          </ul>
        </div>
      );
    }
    if (formStep === 2) {
      // Children Guardians
      return (
        <div className="space-y-4">
          <p>Guardians will step in to care for your children under 18 if everyone with parental responsibility has passed away.</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Don't appoint your partner if they already have parental responsibility.</li>
          </ul>
        </div>
      );
    }
    if (formStep === 3) {
      return "Do you have any pets you would like to provide for?";
    }
    if (formStep === 4) {
      // Pet Guardians
      return (
        <div className="space-y-4">
          <p>You can appoint a guardian to look after your pets if something happens to you. This person will be responsible for their care, feeding, and wellbeing. It's a good idea to choose someone who knows and loves your pets, and to discuss this with them beforehand.</p>
        </div>
      );
    }
  };

  return (
    <StepWrapper
      icon={(formStep === 2 || formStep === 1) ? <Baby className="h-6 w-6" /> : <PawPrint className="h-6 w-6" />}
      title={getGuardianTitle()}
      description={getGuardianDescription()}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register('formStep')} />

        {/* --- STAGE 1: CHILDREN --- */}
        {formStep === 1 && (
          <div className="space-y-4 animate-fade-in">
            {/* Has Children Question */}
            <div className="w-full md:w-1/2 space-y-3">
              <Label className="text-base font-medium text-gray-700">Do you have any children?</Label>
              <div className="flex gap-4 staggered-fade-in">
                <ActionCard selected={hasChildren === true} onClick={() => handleHasChildrenChange(true)}>Yes</ActionCard>
                <ActionCard selected={hasChildren === false} onClick={() => handleHasChildrenChange(false)}>No</ActionCard>
              </div>
              {errors.hasChildren && (
                <p className="text-destructive text-base">{errors.hasChildren.message}</p>
              )}
            </div>

            {/* Children List */}
            {hasChildren === true && (
              <div className="w-full md:w-1/2 space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 gap-3">
                  {childFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex items-center gap-4 p-4 border rounded-xl border-gray-200 bg-white shadow-sm"
                    >
                      <div className="h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 border-primary bg-primary/10 text-primary">
                        <Check className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-base font-semibold text-gray-900 truncate">{field.fullName}</span>
                        <span className="text-sm text-muted-foreground">{field.dateOfBirth}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenEditChildModal(index)}
                        className="p-2 hover:bg-primary/5 rounded-full transition-colors text-gray-400 hover:text-primary"
                        aria-label="Edit child"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <ActionCard showPlusIcon onClick={handleOpenAddChildModal} className="w-full">Add Child</ActionCard>

                {errors.children && !Array.isArray(errors.children) && (
                  <p className="text-destructive text-base">{(errors.children as any).message}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* --- STAGE 2: CHILDREN GUARDIANS --- */}
        {formStep === 2 && (() => {
          // Get minor children (under 18)
          const minorChildren = childFields.filter(child => {
            if (!child.dateOfBirth) return false;
            const parts = child.dateOfBirth.split('/');
            if (parts.length !== 3) return false;
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const year = parseInt(parts[2], 10);
            const dob = new Date(year, month, day);
            const today = new Date();
            let age = today.getFullYear() - dob.getFullYear();
            const m = today.getMonth() - dob.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
              age--;
            }
            return age < 18;
          });

          return (
            <div className="space-y-6 animate-fade-in">
              <div className="w-full md:w-1/2 space-y-10">
                <Label className="text-base font-medium text-gray-700">
                  {minorChildren.length === 1
                    ? `Would you like to appoint guardians for ${minorChildren[0]?.fullName || 'your child'}?`
                    : 'Would you like to appoint guardians for your children?'
                  }
                </Label>
                <div className="flex gap-4 staggered-fade-in">
                  <ActionCard selected={appointGuardians === true} onClick={() => handleAppointGuardiansChange(true)}>Yes</ActionCard>
                  <ActionCard selected={appointGuardians === false} onClick={() => handleAppointGuardiansChange(false)}>No</ActionCard>
                </div>
                {errors.appointGuardians && (
                  <p className="text-destructive text-base">{errors.appointGuardians.message}</p>
                )}
              </div>

              {/* Per-Child Guardian Selection */}
              {appointGuardians === true && (
                <div className="w-full md:w-1/2 space-y-6 animate-fade-in">
                  {minorChildren.map((child, childIndex) => {
                    const childGuardians = getGuardiansForChild(child.fullName);
                    // Use savedPeople directly which now includes manually added people
                    const allPeople = savedPeople
                      .filter(person => person.relationship !== 'Charity') // Charity cannot be a guardian
                      .filter(person => person.fullName !== data.personalDetails?.fullName)
                      // Allow adult children, but not the child themselves or minors
                      .filter(person => {
                        const childRecord = childFields.find(child => child.fullName === person.fullName);
                        if (!childRecord) return true;
                        if (childRecord.fullName === child.fullName) return false; // don't allow self
                        return !isUnder18(childRecord.dateOfBirth); // allow adult children as guardians
                      });

                    return (
                      <div key={child.id || childIndex} className="p-5 border-2 border-gray-200 rounded-xl bg-gray-50/50">
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold text-primary">
                            {child.fullName} - Who would you like their guardian to be?
                          </h3>
                          {(childGuardians as any[])?.length > 0 && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {(childGuardians as any[]).length} guardian{(childGuardians as any[]).length > 1 ? 's' : ''} selected
                            </p>
                          )}
                        </div>

                        {/* Toggle selection cards */}
                        <div className="space-y-3 mb-4">
                          <QuickAddPeople
                            people={allPeople}
                            selectedPeople={[]} // Selection managed by custom isSelected logic
                            onToggle={(person) => toggleGuardianForChild(child.fullName, person)}
                            onEdit={openEditQuickAddModal}
                            isSelected={(personName) => isGuardianSelectedForChild(child.fullName, personName)}
                            heading="" // No heading needed inside this card
                          />
                        </div>

                        <ActionCard showPlusIcon onClick={() => handleOpenAddGuardianModalForChild(child.fullName)} className="w-full">Add Guardian</ActionCard>

                      </div>
                    );
                  })}

                  {errors.guardians && !Array.isArray(errors.guardians) && (
                    <p className="text-destructive text-base">{(errors.guardians as any).message}</p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* --- STAGE 3: PETS --- */}
        {formStep === 3 && (
          <div className="space-y-4 animate-fade-in">
            {/* Has Pets Question */}
            <div className="w-full md:w-1/2 space-y-3">
              <div className="flex gap-4 staggered-fade-in">
                <ActionCard selected={hasPets === true} onClick={() => handleHasPetsChange(true)}>Yes</ActionCard>
                <ActionCard selected={hasPets === false} onClick={() => handleHasPetsChange(false)}>No</ActionCard>
              </div>
              {errors.hasPets && (
                <p className="text-destructive text-base">{errors.hasPets.message}</p>
              )}
            </div>

            {/* Pets List */}
            {hasPets === true && (
              <div className="w-full md:w-1/2 space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 gap-3">
                  {petFields.map((field, index) => (
                    <div
                      key={field.id}
                      className="flex items-center gap-4 p-4 border rounded-xl border-gray-200 bg-white shadow-sm"
                    >
                      <div className="h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 border-primary bg-primary/10 text-primary">
                        <Check className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="text-base font-semibold text-gray-900 truncate">{field.name}</span>
                        <span className="text-sm text-muted-foreground">Pet</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleOpenEditPetModal(index)}
                        className="p-2 hover:bg-primary/5 rounded-full transition-colors text-gray-400 hover:text-primary"
                        aria-label="Edit pet"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <ActionCard showPlusIcon onClick={handleOpenAddPetModal} className="w-full">Add Pet</ActionCard>

                {errors.pets && !Array.isArray(errors.pets) && (
                  <p className="text-destructive text-base">{(errors.pets as any).message}</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* --- STAGE 4: PET GUARDIANS --- */}
        {formStep === 4 && (
          <div className="space-y-10 animate-fade-in">
            <div className="w-full md:w-1/2 space-y-6">
              <Label className="text-base font-medium text-gray-700">
                {petFields.length === 1
                  ? `Would you like to appoint a guardian for ${petFields[0]?.name || 'your pet'}?`
                  : 'Would you like to appoint guardians for your pets?'
                }
              </Label>
              <div className="flex gap-4 staggered-fade-in">
                <ActionCard selected={appointPetGuardians === true} onClick={() => handleAppointPetGuardiansChange(true)}>Yes</ActionCard>
                <ActionCard selected={appointPetGuardians === false} onClick={() => handleAppointPetGuardiansChange(false)}>No</ActionCard>
              </div>
              {errors.appointPetGuardians && (
                <p className="text-destructive text-base">{errors.appointPetGuardians.message}</p>
              )}
            </div>

            {/* Pet Guardians Selection */}
            {appointPetGuardians === true && (
              <div className="w-full md:w-1/2 space-y-4 animate-fade-in">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-medium text-primary">Select Guardians</h3>
                  {(petGuardians as any[])?.length > 0 && (
                    <span className="text-sm font-medium text-gray-600">
                      {(petGuardians as any[]).length}{' '}
                      {(petGuardians as any[]).length === 1 ? 'guardian' : 'guardians'} selected
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  {/* All available people as toggleable cards */}
                  <QuickAddPeople
                    people={savedPeople
                      .filter(person => person.relationship !== 'Charity') // Charity cannot be a guardian
                      .filter(person => person.fullName !== data.personalDetails?.fullName)
                      // Exclude minor children from pet guardian options
                      .filter(person => {
                        const childRecord = childFields.find(child => child.fullName === person.fullName);
                        if (!childRecord) return true;
                        return !isUnder18(childRecord.dateOfBirth);
                      })
                    }
                    selectedPeople={[]}
                    onToggle={togglePetGuardian}
                    onEdit={openEditQuickAddModal}
                    isSelected={isPetGuardianSelected}
                    heading=""
                  />

                  <ActionCard showPlusIcon onClick={handleOpenAddPetGuardianModal} className="w-full">Add Pet Guardian</ActionCard>

                  {errors.petGuardians && !Array.isArray(errors.petGuardians) && (
                    <p className="text-destructive text-base">{(errors.petGuardians as any).message}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <FormNavigation
          onBack={formStep === 1 ? undefined : handleBack}
          isValid={
            // Step 1: Children - need hasChildren selected, and if yes, at least one child
            formStep === 1 ? (
              hasChildren === false || (hasChildren === true && childFields.length > 0)
            ) :
              // Step 2: Guardians - need appointGuardians selected, and if yes, at least one guardian AND confirmation for EACH child with a guardian
              formStep === 2 ? (() => {
                if (appointGuardians === false) return true;
                if ((guardianFields as any[])?.length === 0) return false;

                // Get minor children
                const minorChildren = childFields.filter(child => {
                  if (!child.dateOfBirth) return false;
                  const parts = child.dateOfBirth.split('/');
                  if (parts.length !== 3) return false;
                  const day = parseInt(parts[0], 10);
                  const month = parseInt(parts[1], 10) - 1;
                  const year = parseInt(parts[2], 10);
                  const dob = new Date(year, month, day);
                  const today = new Date();
                  let age = today.getFullYear() - dob.getFullYear();
                  const m = today.getMonth() - dob.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                  return age < 18;
                });

                // Check if every minor child has at least one guardian assigned
                return minorChildren.every(child => {
                  const childGuardians = (guardianFields as any[])?.filter(g => (g as any).forChild === child.fullName);
                  if (!childGuardians || childGuardians.length === 0) return false;
                  return true;
                });
              })() :
                // Step 3: Pets - need hasPets selected, and if yes, at least one pet
                formStep === 3 ? (
                  hasPets === false || (hasPets === true && petFields.length > 0)
                ) :
                  // Step 4: Pet Guardians - need appointPetGuardians selected, and if yes, at least one guardian with valid address
                  formStep === 4 ? (
                    appointPetGuardians === false || (
                      appointPetGuardians === true &&
                      (petGuardians as any[])?.length > 0 &&
                      (petGuardians as any[])?.every(g => !!(g.address?.line1 && g.address?.city && g.address?.postcode))
                    )
                  ) :
                    isValid
          }
        />
      </form>

      <Modal
        isOpen={isAddChildModalOpen}
        onClose={() => {
          setIsAddChildModalOpen(false);
          setEditingChildIndex(null);
          if (childFields.length === 0) {
            setValue('hasChildren', false);
          }
        }}
        title={editingChildIndex !== null ? "Edit Child" : "Add Child"}
      >
        <AddChildForm
          onSave={handleAddChild}
          initialData={editingChildIndex !== null ? childFields[editingChildIndex] : undefined}
          onDelete={editingChildIndex !== null ? () => {
            const fullName = childFields[editingChildIndex]?.fullName;
            handleRemoveChildEntry(editingChildIndex, fullName);
            setEditingChildIndex(null);
            setIsAddChildModalOpen(false);
          } : undefined}
        />
      </Modal>

      <Modal
        isOpen={isAddPetModalOpen}
        onClose={() => {
          setIsAddPetModalOpen(false);
          setEditingPetIndex(null);
          if (petFields.length === 0) {
            setValue('hasPets', false);
          }
        }}
        title={editingPetIndex !== null ? "Edit Pet" : "Add Pet"}
      >
        <AddPetForm
          onSave={handleAddPet}
          initialData={editingPetIndex !== null ? petFields[editingPetIndex] : undefined}
          onDelete={editingPetIndex !== null ? () => {
            removePet(editingPetIndex);
            setEditingPetIndex(null);
            setIsAddPetModalOpen(false);
          } : undefined}
        />
      </Modal>

      <Modal
        isOpen={isAddGuardianModalOpen}
        onClose={() => { setIsAddGuardianModalOpen(false); setGuardianForChild(null); }}
        title={guardianForChild ? `Add Guardian for ${guardianForChild}` : "Add Guardian"}
      >
        <PersonForm
          initialData={{ isOver18: true }}
          onSave={handleAddGuardian}
          showAgeSelection={true}
          hideUnder18={true}
          submitLabel="Save"
        />
      </Modal>
      <Modal
        isOpen={isAddPetGuardianModalOpen}
        onClose={() => setIsAddPetGuardianModalOpen(false)}
        title="Add Pet Guardian"
      >
        <PersonForm
          initialData={{ isOver18: true }}
          onSave={handleAddPetGuardian}
          showAgeSelection={true}
          hideUnder18={true}
          submitLabel="Save"
        />
      </Modal>

      {/* Parental Responsibility Check Modal */}
      <Modal
        isOpen={parentalCheckModalOpen}
        onClose={() => {
          setParentalCheckModalOpen(false);
          setPendingGuardianSelection(null);
        }}
        title="Parental Responsibility Check"
      >
        {pendingGuardianSelection && (
          <div className="space-y-6">
            <p className="text-lg text-gray-700">
              Does <span className="font-semibold">{pendingGuardianSelection.person.fullName}</span> already have parental responsibility for <span className="font-semibold">{pendingGuardianSelection.childName}</span>?
            </p>

            <p className="text-sm text-gray-500">
              Someone with parental responsibility (e.g., a biological parent, adoptive parent, or someone granted it by the courts) doesn&apos;t need to be appointed as a guardian - they already have legal rights to care for the child.
            </p>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => handleParentalResponsibilityAnswer(true)}
                className="flex-1 py-4 px-6 rounded-xl border-2 border-gray-200 bg-white hover:border-amber-400 hover:bg-amber-50 transition-all font-medium text-gray-700"
              >
                Yes, they do
              </button>
              <button
                type="button"
                onClick={() => handleParentalResponsibilityAnswer(false)}
                className="flex-1 py-4 px-6 rounded-xl border-2 border-gray-200 bg-white hover:border-primary hover:bg-primary/5 transition-all font-medium text-gray-700"
              >
                No, they don&apos;t
              </button>
            </div>

            <p className="text-xs text-gray-400 text-center">
              If you&apos;re unsure, it&apos;s usually safe to select &quot;No&quot; - the guardian appointment simply won&apos;t apply if they already have parental responsibility.
            </p>
          </div>
        )}
      </Modal>

      {/* Quick Add Person Edit Modal */}
      <Modal
        isOpen={editingQuickAddPerson !== null && tempQuickAddPerson !== null}
        onClose={handleCloseQuickAddModal}
        title="Edit details"
      >
        {editingQuickAddPerson && tempQuickAddPerson && (
          <PersonForm
            initialData={tempQuickAddPerson}
            onSave={(data) => {
              updatePerson(editingQuickAddPerson.fullName, {
                fullName: data.fullName,
                address: data.address,
                isOver18: data.isOver18
              });
              handleCloseQuickAddModal();
            }}
            onDelete={manuallyAddedPeople.some((p: any) => p.fullName === editingQuickAddPerson.fullName) ? () => {
              removePersonEverywhere(editingQuickAddPerson.fullName);
              handleCloseQuickAddModal();
            } : undefined}
            submitLabel="Save"
            showAgeSelection={true}
          />
        )}
      </Modal>
    </StepWrapper>
  );
};
